import React from 'react';
import { ShieldCheck, FileText, Scale, Lock, Info, AlertCircle, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Section = ({ title, icon: Icon, children }: { title: string, icon: any, children: React.ReactNode }) => (
  <motion.section 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="mb-12"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center text-primary">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-2xl font-black text-foreground">{title}</h2>
    </div>
    <div className="bg-card rounded-3xl p-8 border border-border shadow-xl shadow-foreground/5 text-foreground/70 leading-relaxed font-medium text-justify">
      {children}
    </div>
  </motion.section>
);

export default function TermsAndConditions() {
  return (
    <div className="pt-32 pb-24 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 text-center">
          <span className="text-primary font-black uppercase tracking-[0.4em] text-xs mb-4 block">Legal Dokumentasi</span>
          <h1 className="text-4xl md:text-6xl font-black text-foreground tracking-tighter mb-6 italic">
            Syarat <span className="text-primary">&</span> Ketentuan.
          </h1>
          <p className="text-foreground/40 max-w-2xl mx-auto font-bold uppercase tracking-widest text-[10px]">
            Terakhir Diperbarui: 24 April 2024
          </p>
        </div>

        <div className="space-y-4">
          <Section title="Pendahuluan" icon={Info}>
            <p className="mb-4">
              Selamat datang di Azman204. Dengan mengakses dan menggunakan platform kami, Anda setuju untuk terikat oleh Syarat dan Ketentuan ini. Harap baca dengan seksama sebelum melanjutkan penggunaan layanan kami.
            </p>
            <p>
              Platform ini dikelola untuk memberikan solusi digital bagi administrasi dan dokumentasi jasa konstruksi. Kami berhak mengubah syarat ini kapan saja tanpa pemberitahuan sebelumnya.
            </p>
          </Section>

          <Section title="Penggunaan Layanan" icon={FileText}>
            <p className="mb-4">
              Pengguna bertanggung jawab penuh atas keakuratan data yang diberikan saat pendaftaran maupun saat menggunakan layanan kami.
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Layanan ini hanya ditujukan untuk keperluan profesional dan bisnis yang sah.</li>
              <li>Dilarang mengunggah konten yang melanggar hukum, berbahaya, atau tidak pantas.</li>
              <li>Akun pengguna bersifat pribadi dan tidak diperkenankan untuk dibagikan kepada pihak lain.</li>
            </ul>
          </Section>

          <Section title="Hak Kekayaan Intelektual" icon={ShieldCheck}>
            <p className="mb-4">
              Seluruh aset digital, termasuk namun tidak terbatas pada desain template, kode perangkat lunak, logo, dan konten tertulis di Azman204 adalah milik kami atau pemberi lisensi kami.
            </p>
            <p>
              Pembelian produk digital memberikan lisensi penggunaan kepada Anda, namun tidak mengalihkan hak kepemilikan. Dilarang mendistribusikan ulang atau menjual kembali produk kami tanpa izin tertulis.
            </p>
          </Section>

          <Section title="Pembayaran & Pengembalian" icon={Scale}>
            <p className="mb-4">
              Semua transaksi pembayaran dilakukan melalui metode yang telah kami sediakan secara resmi di platform.
            </p>
            <p>
              Mengingat sifat produk kami adalah produk digital (non-fisik), harap dipahami bahwa <strong>tidak ada kebijakan pengembalian dana (refund)</strong> setelah produk dibeli atau diakses, kecuali terjadi kesalahan sistem yang dikonfirmasi oleh tim kami.
            </p>
          </Section>

          <Section title="Keamanan Data" icon={Lock}>
            <p>
              Kami berkomitmen untuk menjaga keamanan data pribadi Anda sesuai dengan Kebijakan Privasi kami. Namun, pengguna juga bertanggung jawab untuk menjaga kerahasiaan kata sandi dan akses akun mereka sendiri.
            </p>
          </Section>

          <Section title="Batasan Tanggung Jawab" icon={AlertCircle}>
            <p>
              Azman204 tidak bertanggung jawab atas kerugian langsung atau tidak langsung yang timbul dari penyalahgunaan produk digital kami atau kegagalan bisnis pengguna. Produk kami disediakan "sebagaimana adanya" untuk membantu administrasi Anda.
            </p>
          </Section>
        </div>

        {/* WhatsApp CTA */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-24 p-8 md:p-12 rounded-[2.5rem] bg-green-500/5 relative overflow-hidden border border-green-500/10 flex flex-col items-center text-center"
        >
          {/* Watermark Logo */}
          <div className="absolute top-1/2 right-0 -translate-y-1/2 pointer-events-none opacity-[0.05]">
            <MessageCircle className="w-64 h-64 text-green-500" />
          </div>
          
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-card shadow-sm flex items-center justify-center text-[#25d366] mb-6 mx-auto">
              <MessageCircle className="w-6 h-6 fill-current" />
            </div>
            
            <h3 className="text-2xl md:text-3xl font-black text-foreground mb-4">Butuh Respons Cepat?</h3>
            <p className="text-foreground/60 max-w-sm mx-auto font-medium mb-10 text-base md:text-lg">
              Hubungi admin kami langsung melalui pesan WhatsApp.
            </p>
            
            <a 
              href="https://wa.me/6281261089400" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25d366] text-white px-8 py-4 md:px-10 md:py-5 rounded-2xl font-black text-lg hover:scale-105 active:scale-95 transition-all shadow-[0_10px_30px_rgba(37,211,102,0.2)]"
            >
              <MessageCircle className="w-6 h-6 fill-current" />
              Chat via WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
